"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";

import AuthCard from "@/components/shared/auth-card";
import { shouldReportAuthFlowExceptionToSentry } from "@/lib/auth-flow-sentry-filter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { createClient } from "@/lib/supabase/client";

type Mode = "password" | "magic";

function Toggle({ mode, setMode }: { mode: Mode; setMode: (m: Mode) => void }) {
  return (
    <div className="mb-6 flex gap-1 rounded-lg bg-background p-1">
      <button
        type="button"
        onClick={() => setMode("password")}
        className={[
          "flex-1 rounded-md px-3 py-2 text-sm transition-colors",
          mode === "password"
            ? "bg-card text-foreground font-medium shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        ].join(" ")}
      >
        Password
      </button>
      <button
        type="button"
        onClick={() => setMode("magic")}
        className={[
          "flex-1 rounded-md px-3 py-2 text-sm transition-colors",
          mode === "magic"
            ? "bg-card text-foreground font-medium shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        ].join(" ")}
      >
        Magic link
      </button>
    </div>
  );
}

export default function LoginPage() {
  const router = useRouter();
  const supabase = useMemo(() => createClient(), []);

  const [mode, setMode] = useState<Mode>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      if (mode === "password") {
        const { data, error: authError } =
          await supabase.auth.signInWithPassword({
            email: email.trim(),
            password,
          });

        if (authError) {
          setError(authError.message);
          return;
        }

        if (data.session) {
          if (data.user?.id) {
            posthog.identify(data.user.id, { email: data.user.email });
          }
          router.push("/dashboard");
        }
      } else {
        const { error: authError } = await supabase.auth.signInWithOtp({
          email: email.trim(),
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`,
          },
        });

        if (authError) {
          setError(authError.message);
          return;
        }

        setSent(true);
      }
    } catch (err) {
      if (shouldReportAuthFlowExceptionToSentry(err)) {
        Sentry.captureException(err);
      }
      const message = err instanceof Error ? err.message : "Unexpected error";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <AuthCard title="Welcome back" subtitle="Sign in to your account">
        <Toggle mode={mode} setMode={setMode} />

        {mode === "magic" && sent ? (
          <p className="text-center text-sm text-muted-foreground">
            Check your inbox — we sent you a magic link ✨
          </p>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <Input
              type="email"
              placeholder="Enter your email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-11"
            />

            {mode === "password" ? (
              <Input
                type="password"
                placeholder="Enter your Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11"
              />
            ) : null}

            {error ? <p className="text-sm text-destructive">{error}</p> : null}

            <Button
              type="submit"
              className="h-11 w-full bg-primary text-base font-medium text-primary-foreground hover:bg-primary/90"
              disabled={submitting}
            >
              {mode === "password" ? "Sign in" : "Send magic link"}
            </Button>

            <p className="mt-4 text-center text-sm text-muted-foreground">
              Don&apos;t have an account?{" "}
              <Link
                href="/signup"
                className="font-medium text-primary hover:underline"
              >
                Sign up →
              </Link>
            </p>
          </form>
        )}
      </AuthCard>
    </>
  );
}

